import { useEffect, useRef, useState } from 'react';

/**
 * Бесконечная подгрузка вместо кнопки «Показать ещё»: следит за элементом-
 * маркером в конце списка и зовёт `onLoadMore`, когда тот подходит к области
 * просмотра. Это поведение Lampa — список растёт по мере прокрутки сам.
 *
 * `enabled` — есть ли что грузить и не идёт ли уже загрузка (иначе маркер
 * выстрелил бы очередью запросов). `rearmKey` пересоздаёт наблюдатель после
 * каждой догрузки: если после неё маркер всё ещё виден (контент не заполнил
 * экран), подгрузка продолжится, а как только заполнит — остановится сама.
 *
 * `rootMargin` берёт запас в 600px, чтобы следующая страница успела прийти до
 * того, как пользователь упрётся в конец, и прокрутка не дёргалась.
 */
export function useInfiniteScroll(
  onLoadMore: () => void,
  options: { enabled: boolean; rearmKey: unknown },
): (node: HTMLElement | null) => void {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const enabledRef = useRef(options.enabled);
  enabledRef.current = options.enabled;

  const [node, setNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (node === null) return;
    // Наблюдателя может не быть вовсе: старый браузер телевизора, jsdom в
    // тестах. Тогда грузим сразу — список, который не догружается никогда,
    // хуже списка, который догрузился раньше времени.
    if (typeof IntersectionObserver === 'undefined') {
      if (enabledRef.current) onLoadMoreRef.current();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && enabledRef.current) onLoadMoreRef.current();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // rearmKey в зависимостях: после догрузки наблюдатель переоценит видимость.
  }, [node, options.enabled, options.rearmKey]);

  return setNode;
}
